import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Row,
  Col,
  Space,
  message,
  Typography,
} from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import type { HRContractType, ContractTypeFormData } from "../../types/hr";
import { useHRAPI } from "../../hooks/useHRAPI";

const { Title } = Typography;
const { TextArea } = Input;

interface ContractTypeFormProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  contractType?: HRContractType | null;
}

const ContractTypeForm: React.FC<ContractTypeFormProps> = ({
  visible,
  onCancel,
  onSuccess,
  contractType,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const { createContractType, updateContractType } = useHRAPI();

  const isEditing = Boolean(contractType?.id);

  // Remplir le formulaire si on édite
  useEffect(() => {
    if (visible && contractType) {
      form.setFieldsValue({
        name: contractType.name,
        code: contractType.code,
        description: contractType.description,
        max_duration_months: contractType.max_duration_months,
        trial_period_days: contractType.trial_period_days,
        notice_period_days: contractType.notice_period_days,
        renewable: contractType.renewable,
        is_active: contractType.is_active,
      });
    } else if (visible) {
      // Valeurs par défaut pour un nouveau type
      form.setFieldsValue({
        notice_period_days: 30,
        trial_period_days: 0,
        renewable: true,
        is_active: true,
      });
    }
  }, [visible, contractType, form]);

  // Réinitialiser le formulaire quand le modal se ferme
  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const formData: ContractTypeFormData = {
        name: values.name,
        code: values.code || null,
        description: values.description || null,
        max_duration_months: values.max_duration_months || null,
        trial_period_days: values.trial_period_days || 0,
        notice_period_days: values.notice_period_days || 30,
        renewable: Boolean(values.renewable),
        is_active: Boolean(values.is_active),
      };

      if (isEditing && contractType?.id) {
        await updateContractType(contractType.id, formData);
        message.success("Type de contrat modifié avec succès");
      } else {
        await createContractType(formData);
        message.success("Type de contrat créé avec succès");
      }

      onSuccess();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      if (error instanceof Error) {
        message.error(`Erreur: ${error.message}`);
      } else {
        message.error("Erreur lors de la sauvegarde du type de contrat");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {isEditing
            ? "Modifier le type de contrat"
            : "Nouveau type de contrat"}
        </Title>
      }
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="cancel" icon={<CloseOutlined />} onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSubmit}
        >
          {isEditing ? "Modifier" : "Créer"}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false} size="large">
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              label="Nom du type de contrat"
              name="name"
              rules={[
                { required: true, message: "Le nom est obligatoire" },
                { min: 2, message: "Le nom doit faire au moins 2 caractères" },
                {
                  max: 100,
                  message: "Le nom ne peut pas dépasser 100 caractères",
                },
              ]}
            >
              <Input placeholder="Ex: CDI, CDD, Stage..." />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Code (optionnel)"
              name="code"
              rules={[
                {
                  max: 10,
                  message: "Le code ne peut pas dépasser 10 caractères",
                },
              ]}
            >
              <Input placeholder="Ex: CDI, CDD..." />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              max: 500,
              message: "La description ne peut pas dépasser 500 caractères",
            },
          ]}
        >
          <TextArea rows={3} placeholder="Description du type de contrat..." />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Durée maximale (mois)"
              name="max_duration_months"
              help="Laisser vide pour une durée illimitée"
            >
              <InputNumber
                min={1}
                max={120}
                placeholder="Ex: 12"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Période d'essai (jours)"
              name="trial_period_days"
              rules={[
                {
                  type: "number",
                  min: 0,
                  message: "Doit être positif ou zéro",
                },
              ]}
            >
              <InputNumber
                min={0}
                max={365}
                placeholder="0"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Préavis de rupture (jours)"
              name="notice_period_days"
              rules={[
                { required: true, message: "Le préavis est obligatoire" },
                {
                  type: "number",
                  min: 0,
                  message: "Doit être positif ou zéro",
                },
              ]}
            >
              <InputNumber
                min={0}
                max={365}
                placeholder="30"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Renouvelable"
              name="renewable"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Actif" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ContractTypeForm;
